<template>
  <div class="provision-query app-main-content">
    <!-- 操作按钮 -->
    <el-card class="toolbar-card" shadow="never">
      <el-button
        v-show="$checkPermission(['role_queryProvision'])"
        icon="el-icon-chat-dot-round"
        @click="handleSubmitObjection"
      >
        提出异议
      </el-button>
      <el-button
        v-show="$checkPermission(['role_adjustProvision'])"
        type="primary"
        icon="el-icon-edit"
        @click="handleAdjustAmount"
      >
        调整计提金额
      </el-button>
    </el-card>

    <!-- 搜索区域 -->
    <el-card class="search-card" shadow="never">
      <el-form :model="queryParams" inline size="small">
        <el-form-item label="计提月份">
          <el-date-picker
            v-model="queryParams.provisionDateStart"
            type="month"
            placeholder="开始月份"
            value-format="yyyy-MM-dd"
            style="width: 130px"
          />
          <span style="margin: 0 5px">至</span>
          <el-date-picker
            v-model="queryParams.provisionDateEnd"
            type="month"
            placeholder="结束月份"
            value-format="yyyy-MM-dd"
            style="width: 130px"
          />
        </el-form-item>
        <el-form-item label="业务月份">
          <el-date-picker
            v-model="queryParams.businessDateStart"
            type="month"
            placeholder="开始月份"
            value-format="yyyy-MM-dd"
            style="width: 130px"
          />
          <span style="margin: 0 5px">至</span>
          <el-date-picker
            v-model="queryParams.businessDateEnd"
            type="month"
            placeholder="结束月份"
            value-format="yyyy-MM-dd"
            style="width: 130px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 数据表格 -->
    <el-card shadow="never">
      <cargo-ag-grid
        ref="agGrid"
        :config-module="provisionConfigModule"
        :grid-options="gridOptions"
        :column-defs="finalColumnDefs"
        :row-data="rowData"
        :filter-menu-items="filterMenuItems"
        style="width: 100%; height: 100%;"
        @gridReady="onGridReady"
        @selection-changed="onSelectionChanged"
      />

      <!-- 分页 -->
      <cargo-pagination
        class="pagination"
        :total="total"
        :page.sync="queryParams.page"
        :limit.sync="queryParams.pageSize"
        @pagination="getList"
      />
    </el-card>

    <!-- 提出异议弹窗 -->
    <objection-dialog
      :visible.sync="objectionDialogVisible"
      @success="handleObjectionSuccess"
    />

    <!-- 调整金额弹窗 -->
    <adjust-dialog
      :visible.sync="adjustDialogVisible"
      :selected-rows="selectedRows"
      @success="handleAdjustSuccess"
    />
  </div>
</template>

<script>
import { queryProvisionDetail, submitObjection } from '@/api/provision/provisionData'
import ObjectionDialog from './components/ObjectionDialog.vue'
import AdjustDialog from './components/AdjustDialog.vue'
import { UICustomUtil } from '@/class/cargo/model/utils/UICustomUtil'
import * as UICustomConstants from '@/class/cargo/model/parameters/UICustomConstants'
import * as ProvisionConstants from '@/class/cargo/model/parameters/ProvisionConstants'

export default {
  name: 'ProvisionQuery',
  components: {
    ObjectionDialog,
    AdjustDialog
  },
  data() {
    return {
      // ========== 查询参数 ==========
      queryParams: {
        stationName: '',
        provisionUuid: '',
        provisionDateStart: '',
        provisionDateEnd: '',
        businessDateStart: '',
        businessDateEnd: '',
        page: 1,
        pageSize: 100
      },

      // ========== 表格数据 ==========
      rowData: [],
      total: 0,
      gridApi: null,

      // ========== 复选框选中数据 ==========
      selectedRows: [],

      // ========== 弹窗控制 ==========
      objectionDialogVisible: false,
      adjustDialogVisible: false,

      // ========== AG Grid配置 ==========
      gridOptions: {
        // 启用多选模式
        rowSelection: 'multiple',
        // 禁止点击行选中
        suppressRowClickSelection: true,
        getRowHeight: params => this.getProvisionRowHeight(params.data)
      },

      // 计提查询配置模块（使用字符串，与cargoAgGrid组件的configModule属性类型对应）
      provisionConfigModule: String(ProvisionConstants.PROVISION_DATA_QUERY),

      // ========== 表格列定义 ==========
      defaultColumnDefs: [
        // 复选框列配置（固定在左侧第一列）
        {
          headerCheckboxSelection: true, // 表头显示全选复选框
          checkboxSelection: true, // 每行显示复选框
          showDisabledCheckboxes: true, // 显示禁用状态的复选框
          width: 50, // 列宽
          pinned: 'left', // 固定在左侧
          suppressSizeToFit: true, // 禁止自动调整大小
          sortable: false, // 禁止排序
          resizable: false, // 禁止拖拽调整宽度
          suppressMovable: true // 禁止移动列位置
        },

        // 业务数据列
        { field: 'dptName', headerName: '部门名称', width: 150 },
        { field: 'roleName', headerName: '岗位角色', width: 120 },
        { field: 'userName', headerName: '姓名', width: 100 },
        {
          field: 'provisionDate',
          headerName: '计提月份',
          width: 120,
          valueFormatter: params => this.formatDate(params.value)
        },
        {
          field: 'businessDate',
          headerName: '业务月份',
          width: 120,
          valueFormatter: params => this.formatDate(params.value)
        },
        { field: 'businessNum', headerName: '业务票数', width: 100 },
        { field: 'teu', headerName: 'TEU', width: 100 },
        {
          field: 'grossProfitTotal',
          headerName: '计息毛利总额',
          width: 120,
          tooltipValueGetter: () => '',
          valueFormatter: params => {
            if (!params.data) return ''
            // 管理员（计提生成权限）可见；可配置岗位可见；其他不显示
            const isAdmin = this.$checkPermission(['role_generateProvision'])
            const isConfigurable = ProvisionConstants.isConfigurableStation(params.data.stationName)
            if (!isAdmin && !isConfigurable) return ''
            return this.formatAmount(params.value)
          }
        },
        {
          field: 'provisionAmount',
          headerName: '计提金额',
          width: 120,
          valueFormatter: params => this.formatAmount(params.value)
        },
        {
          field: 'updateAmount',
          headerName: '调整后金额',
          width: 120,
          valueFormatter: params => this.formatAmount(params.value)
        },
        {
          field: 'ruleDetailDisplay',
          headerName: '规则明细',
          width: 400,
          autoHeight: true,
          cellClass: 'rule-detail-ag-cell',
          // 禁用默认的 title 悬浮提示，避免原始 JSON 泄露给无权限用户
          tooltipValueGetter: () => '',
          cellRenderer: params => {
            // 仅管理员（计提生成权限）可见
            if (!this.$checkPermission(['role_generateProvision'])) return ''
            return this.formatRuleDetailHtml(params.data)
          }
        },
        { field: 'adjustReason', headerName: '调整原因', width: 150 },
        { field: 'objectionReason', headerName: '异议原因', width: 150 },
        {
          headerName: '对应业务',
          width: 100,
          cellRenderer: params => {
            if (!params.data.uuid) return ''
            // 检查是否有计提查询菜单权限
            const hasQueryPermission = this.$checkPermission(['module_provisionQuery'])
            if (!hasQueryPermission) return ''
            return `<a href="javascript:void(0)" style="color: #409EFF; cursor: pointer;" onclick="window.viewJob('${params.data.uuid}')">详情</a>`
          }
        }
      ]
    }
  },
  computed: {
    finalColumnDefs() {
      // 应用UI自定义配置（如果启用）
      if (this.$store.state.uiCustom && this.$store.state.uiCustom.enable) {
        return UICustomUtil.handleColumnDefs(
          ProvisionConstants.PROVISION_DATA_MODULE,
          this.$store.getters.currentOfficeId,
          this.defaultColumnDefs
        )
      }
      return this.defaultColumnDefs
    }
  },
  created() {
    // 暴露全局方法供ag-grid调用
    window.viewJob = this.viewJob

    // 加载参数并查询
    this.loadParamsAndQuery()

    // 加载UI配置
    if (this.$store.state.uiCustom && this.$store.dispatch) {
      this.$store.dispatch('uiCustom/loadUICustomPageInfo', {
        pageType: ProvisionConstants.PROVISION_DATA_QUERY,
        branchId: this.$store.getters.currentOfficeId
      }).catch(() => {
        // UI配置加载失败不影响页面显示
      })
    }
  },
  activated() {
    // 当使用 keep-alive 缓存组件时，切换标签页会触发 activated
    // 重新加载参数并查询数据
    this.loadParamsAndQuery()
  },
  beforeDestroy() {
    delete window.viewJob
  },
  methods: {
    // ========== 数据查询方法 ==========

    /**
     * 加载参数并查询数据
     */
    loadParamsAndQuery() {
      // 从路由参数获取查询条件（支持范围查询）
      if (this.$route.query.stationName) {
        this.queryParams.stationName = this.$route.query.stationName
      }
      if (this.$route.query.provisionDate) {
        // 兼容旧的单值查询
        const date = this.parseDate(this.$route.query.provisionDate)
        this.queryParams.provisionDateStart = date
        this.queryParams.provisionDateEnd = date
      }
      if (this.$route.query.businessDate) {
        // 兼容旧的单值查询
        const date = this.parseDate(this.$route.query.businessDate)
        this.queryParams.businessDateStart = date
        this.queryParams.businessDateEnd = date
      }
      // 处理 uuid 参数（用于精确定位某条记录）
      if (this.$route.query.uuid) {
        console.log('接收到 uuid 参数:', this.$route.query.uuid)
      }

      // 从 sessionStorage 读取参数（从计提生成页面跳转时携带）
      const storedParams = sessionStorage.getItem('provisionQueryParams')
      if (storedParams) {
        try {
          const params = JSON.parse(storedParams)
          if (params.stationName) {
            this.queryParams.stationName = params.stationName
          }
          if (params.provisionDate) {
            const date = this.parseDate(params.provisionDate)
            this.queryParams.provisionDateStart = date
            this.queryParams.provisionDateEnd = date
          }
          if (params.businessDate) {
            const date = this.parseDate(params.businessDate)
            this.queryParams.businessDateStart = date
            this.queryParams.businessDateEnd = date
          }
          if (params.uuid) {
            // 将 uuid 赋值给 provisionUuid 参数
            this.queryParams.provisionUuid = params.uuid
            console.log('从 sessionStorage 接收到 uuid 参数，已赋值给 provisionUuid:', params.uuid)
          }
          // 使用后清除参数
          sessionStorage.removeItem('provisionQueryParams')
        } catch (e) {
          console.error('解析 sessionStorage 参数失败', e)
        }
      }

      // 查询数据
      this.getList()
    },

    /**
     * 获取列表数据
     */
    async getList() {
      const params = {
        stationName: this.queryParams.stationName || undefined,
        provisionUuid: this.queryParams.provisionUuid || undefined,
        provisionDateStart: this.queryParams.provisionDateStart || undefined,
        provisionDateEnd: this.queryParams.provisionDateEnd || undefined,
        businessDateStart: this.queryParams.businessDateStart || undefined,
        businessDateEnd: this.queryParams.businessDateEnd || undefined,
        pageNum: this.queryParams.page,
        pageSize: this.queryParams.pageSize
      }

      const res = await queryProvisionDetail(params)
      if (res.ok && res.data) {
        this.rowData = res.data.list || []
        this.total = res.data.total || 0
        this.$nextTick(() => {
          if (this.gridApi) {
            this.gridApi.resetRowHeights()
          }
        })
      }
    },

    /**
     * 查询按钮点击事件
     */
    handleSearch() {
      this.queryParams.page = 1
      this.getList()
    },

    /**
     * 重置按钮点击事件
     */
    handleReset() {
      this.queryParams = {
        provisionDateStart: '',
        provisionDateEnd: '',
        businessDateStart: '',
        businessDateEnd: '',
        page: 1,
        pageSize: 100
      }
      this.getList()
    },

    // ========== 复选框相关方法 ==========

    /**
     * 复选框选择变化事件
     * @param {Object} event - AG Grid选择事件对象
     */
    onSelectionChanged(event) {
      // 获取当前选中的所有行数据
      this.selectedRows = event.api.getSelectedRows()
    },

    onGridReady(params) {
      this.gridApi = params.api
    },

    getProvisionRowHeight(row) {
      if (!row || !row.ruleDetailJson || !this.$checkPermission(['role_generateProvision'])) {
        return 32
      }
      try {
        const lines = JSON.parse(row.ruleDetailJson)
        if (!Array.isArray(lines) || !lines.length) return 32
        return Math.min(32 + (lines.length - 1) * 20, 88)
      } catch (e) {
        return 32
      }
    },

    // ========== 批量操作方法 ==========

    /**
     * 提出异议（打开弹窗）
     */
    handleSubmitObjection() {
      if (!this.selectedRows.length) {
        this.$message.warning('请选择要提出异议的记录')
        return
      }

      // 单选模式：只能选择一条记录
      if (this.selectedRows.length > 1) {
        this.$message.warning('只能选择一条记录进行调整')
        return
      }

      this.objectionDialogVisible = true
    },

    /**
     * 提出异议成功回调
     */
    async handleObjectionSuccess() {
      this.$message.success('提交成功')
      this.objectionDialogVisible = false
      this.getList()
    },

    /**
     * 调整计提金额（打开弹窗）
     */
    handleAdjustAmount() {
      if (!this.selectedRows.length) {
        this.$message.warning('请选择要调整的记录')
        return
      }

      // 单选模式：只能选择一条记录
      if (this.selectedRows.length > 1) {
        this.$message.warning('只能选择一条记录进行调整')
        return
      }

      // TODO: 校验发布状态，需要从后端返回publish_state字段
      // const invalidRows = this.selectedRows.filter(row => row.publishState === '1')
      // if (invalidRows.length > 0) {
      //   this.$message.error('已发布状态的记录无法执行此操作')
      //   return
      // }

      this.adjustDialogVisible = true
    },

    /**
     * 调整金额成功回调
     */
    async handleAdjustSuccess() {
      this.$message.success('调整成功')
      this.adjustDialogVisible = false
      this.getList()
    },

    // ========== 工具方法 ==========

    /**
     * 查看对应业务详情
     * @param {String} detailUuid - 计提明细主键UUID
     */
    viewJob(detailUuid) {
      this.$router.push({
        name: 'JobDetail',
        query: { detailUuid }
      })
    },

    /**
     * 格式化日期为年月格式（yyyy-MM）
     * @param {Date|String} date - 日期对象或字符串
     * @returns {String} 格式化后的日期字符串
     */
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      return `${year}-${month}`
    },

    /**
     * 解析日期字符串为yyyy-MM-dd格式
     * @param {String} dateStr - 日期字符串（格式：yyyy-MM）
     * @returns {String} yyyy-MM-dd格式的日期字符串
     */
    parseDate(dateStr) {
      if (!dateStr) return ''
      const parts = dateStr.split('-')
      if (parts.length >= 2) {
        // 将yyyy-MM转换为yyyy-MM-01格式
        return `${parts[0]}-${parts[1]}-01`
      }
      return ''
    },

    /**
     * 过滤右键菜单项，只保留Excel导出选项
     * @param {Array} menus - 原始菜单项数组
     * @returns {Array} 过滤后的菜单项数组
     */
    filterMenuItems(menus) {
      // 只保留包含"导出Excel"的菜单项
      return menus.filter(menu => {
        if (typeof menu === 'object' && menu.name) {
          return menu.name.indexOf('导出Excel') !== -1
        }
        return false
      })
    },

    /**
     * 格式化金额为两位小数
     * @param {Number} amount - 金额
     * @returns {String} 格式化后的金额字符串
     */
    formatAmount(amount) {
      if (amount === null || amount === undefined) return ''
      return Number(amount).toFixed(2)
    },

    formatRuleDetail(row) {
      if (!row) return ''
      if (row.ruleDetailJson) {
        try {
          const lines = JSON.parse(row.ruleDetailJson)
          if (Array.isArray(lines) && lines.length) {
            return lines.map(item => {
              const name = item.ruleName || ''
              const amount = item.amount != null ? Number(item.amount).toFixed(2) : ''
              return amount ? `${name}: ${amount}` : name
            }).join('；')
          }
        } catch (e) {
          return row.ruleDetailJson
        }
      }
      if (row.rule === 'configurable') {
        return ''
      }
      return row.rule || ''
    },
    /**
     * 规则明细 HTML 渲染：所有规则单行拼接显示，分号分隔
     * 数据结构示例：[{ ruleName, formula, amount }]
     */
    formatRuleDetailHtml(row) {
      if (!row || !row.ruleDetailJson) return ''
      let lines = []
      try {
        const parsed = JSON.parse(row.ruleDetailJson)
        if (Array.isArray(parsed)) {
          lines = parsed
        }
      } catch (e) {
        return ''
      }
      if (!lines.length) return ''
      const escapeHtml = (s) => String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      // 每条规则单独一行，避免公式过长时挤在同一个单元格内
      const segments = lines.map(item => {
        const name = escapeHtml(item.ruleName || '')
        const formula = escapeHtml(this.formatFormula(item.formula || item.calcFormula || ''))
        const amount = item.amount != null ? Number(item.amount).toFixed(2) : ''
        let content
        if (formula && amount) {
          content = `<span class="rd-name">${name}</span>：<span class="rd-formula">${formula}</span>`
        } else if (amount) {
          content = `<span class="rd-name">${name}</span>：<span class="rd-amount">${amount}</span>`
        } else if (formula) {
          content = `<span class="rd-name">${name}</span>：<span class="rd-formula">${formula}</span>`
        } else {
          content = `<span class="rd-name">${name}</span>`
        }
        return `<div class="rd-line">${content}</div>`
      })
      return `<div class="rule-detail-cell">${segments.join('')}</div>`
    },
    formatFormula(formula) {
      if (!formula) return ''
      return String(formula).replace(/(总计息毛利|组内他人毛利|计息毛利)(-?\d+(?:\.\d+)?)/g, (match, label, amount) => {
        return label + Number(amount).toFixed(2)
      })
    }
  }
}
</script>

<style scoped lang="scss">
.provision-query {
  padding: 10px;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 100px);
  overflow: hidden;

  .search-card {
    margin-bottom: 10px;
    flex-shrink: 0;
  }

  .toolbar-card {
    margin-bottom: 10px;
    flex-shrink: 0;
  }

  ::v-deep .el-card__body {
    padding-bottom: 10px;
  }

  .pagination {
    margin-top: 10px;
    text-align: right;
    flex-shrink: 0;
  }

  ::v-deep .el-card {
    flex-shrink: 0;
  }

  // 让数据表格卡片占据剩余空间
  > .el-card:nth-child(3) {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 0;

    ::v-deep .el-card__body {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      padding-bottom: 10px;
      min-height: 400px;
    }
  }

  // AG Grid 空状态样式优化
  ::v-deep .ag-overlay-no-rows-wrapper {
    height: 100% !important;

    .ag-overlay-loading-center {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      background: transparent;
      border: none;

      > div {
        padding: 50px 80px;
        background: #F5F7FA;
        border: 2px dashed #DCDFE6;
        border-radius: 8px;
        min-width: 300px;

        i {
          font-size: 16px;
          color: #909399;
          font-style: normal;
        }
      }
    }
  }
}
</style>

<style>
/* ag-grid 动态渲染的内容 scoped 样式不生效，需要全局样式 */
.rule-detail-cell {
  display: block;
  white-space: normal;
  overflow-y: auto;
  overflow-x: hidden;
  font-size: 12px;
  line-height: 18px;
  max-height: 72px;
  padding: 2px 0;
}
.rule-detail-cell .rd-line {
  display: block;
  margin-bottom: 2px;
  word-break: break-all;
}
.rule-detail-cell .rd-name {
  color: #303133;
  font-weight: 500;
}
.rule-detail-cell .rd-formula {
  color: #909399;
}
.rule-detail-cell .rd-amount {
  color: #67C23A;
  font-weight: 500;
}
</style>
